import { useCallback, useState, type ReactNode } from 'react';
import { Color, Size, sameNameSuggestion, t, type SameNameAnswer } from '@bms2/shared';
import { Button } from '../../components/controls';
import { get } from './api';
import styles from './SameNameQuestion.module.css';

/** 🔒 The question's route (decision 2421): the document on the record whose name the upload already is. */
export const FILE_SAME_NAME_PATH = '/files/same-name';

/** The standing document the route answers with. */
export interface SameNameStanding {
  id: string;
  filename: string;
  fileTypeId: string;
  fileTypeName: string;
}

interface Asked {
  name: string;
  standing: SameNameStanding;
  suggestion: SameNameAnswer;
  answer: (choice: SameNameAnswer) => void;
}

/**
 * 🔒 The same-name question (decision 2421): before a generic upload sends a
 * file whose name is already a document's on the record, the window asks
 * whether it is that document's new version or a separate file, and suggests
 * the answer: the new version when the picked type matches the standing
 * document's, a separate file otherwise. `check` answers the standing document
 * to version, or null to send a new file; Stop ends the wait as a separate
 * file never sent, because the aborted request then sends nothing.
 */
export function useSameNameQuestion(apiBaseUrl: string): { check: (recordType: string, recordId: string, name: string, fileTypeId: string, signal: AbortSignal) => Promise<SameNameStanding | null>; question: ReactNode } {
  const [asked, setAsked] = useState<Asked | null>(null);

  const check = useCallback(
    async (recordType: string, recordId: string, name: string, fileTypeId: string, signal: AbortSignal): Promise<SameNameStanding | null> => {
      const query = new URLSearchParams({ taggedRecordType: recordType, taggedRecordId: recordId, filename: name });
      const found = await get<{ standing: SameNameStanding | null }>(`${FILE_SAME_NAME_PATH}?${query.toString()}`, apiBaseUrl, signal);
      // 🔨 Stop during the check draws no question: the upload settles at once, and its aborted request sends nothing.
      if (signal.aborted) return null;
      // 🔨 A question the server could not answer asks nothing: the file goes as a separate file, which loses nothing.
      if (!found.ok || !found.data.standing) return null;
      const standing = found.data.standing;
      const choice = await new Promise<SameNameAnswer>((resolve) => {
        const settle = (answer: SameNameAnswer) => {
          signal.removeEventListener('abort', stopped);
          setAsked(null);
          resolve(answer);
        };
        const stopped = () => settle('separate');
        signal.addEventListener('abort', stopped);
        setAsked({ name, standing, suggestion: sameNameSuggestion(fileTypeId, standing.fileTypeId), answer: settle });
      });
      return choice === 'version' ? standing : null;
    },
    [apiBaseUrl],
  );

  const question = asked ? (
    <div className={styles.question} data-same-name={asked.standing.id} data-same-name-suggests={asked.suggestion}>
      <p className={styles.text}>
        {t('upload.sameName.lead')} <strong>{asked.name}</strong> {t('upload.sameName.already')} {t('upload.sameName.ask')}
      </p>
      <p className={styles.hint}>
        {t('upload.sameName.filedAs')} {asked.standing.fileTypeName}
      </p>
      <p className={styles.hint}>{asked.suggestion === 'version' ? t('upload.sameName.suggestVersion') : t('upload.sameName.suggestSeparate')}</p>
      <div className={styles.answers}>
        <span data-same-name-answer="version">
          <Button size={Size.Small} color={asked.suggestion === 'version' ? Color.Navy : Color.Inactive} text={[t('upload.sameName.version')]} actions={[() => asked.answer('version')]} />
        </span>
        <span data-same-name-answer="separate">
          <Button size={Size.Small} color={asked.suggestion === 'separate' ? Color.Navy : Color.Inactive} text={[t('upload.sameName.separate')]} actions={[() => asked.answer('separate')]} />
        </span>
      </div>
    </div>
  ) : null;

  return { check, question };
}
